import { useEffect, useState, type ClipboardEvent, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import {
  AUTHORIZATION_CRITERIA_OPTIONS,
  type AuthorizationCriteria,
  type FhirIntegrationInput,
  type FhirIntegrationViewModel,
} from '../integrationApi';

interface Draft {
  name: string;
  isDefault: boolean;
  active: boolean;
  skipAuthentication: boolean;
  authorizationCriteria: AuthorizationCriteria;
  patientIdHeaderName: string;
  accessTokenHeaderName: string;
  frontendBaseUrl: string;
  patientIdSearchCriteria: string;
  patientIdExtensionUrl: string;
  fhirServiceRootUrl: string;
  systemAccountUser: string;
  systemAccountPass: string;
  auth0TokenUrl: string;
  auth0ApiAudience: string;
  auth0Connection: string;
  auth0ClientId: string;
  auth0ClientSecret: string;
  authorizationHost: string;
  smartClientId: string;
  smartClientSecret: string;
  smartRedirectUri: string;
  smartScopes: string;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  isDefault: false,
  active: true,
  skipAuthentication: false,
  authorizationCriteria: 'REFRESH_TOKEN',
  // Carry the backend code-defaults so a fresh entry pre-fills the well-known header names.
  patientIdHeaderName: 'Gene42-Fhir-PatientId',
  accessTokenHeaderName: 'Gene42-Fhir-AccessToken',
  frontendBaseUrl: '',
  patientIdSearchCriteria: '',
  patientIdExtensionUrl: '',
  fhirServiceRootUrl: '',
  systemAccountUser: '',
  systemAccountPass: '',
  auth0TokenUrl: '',
  auth0ApiAudience: '',
  auth0Connection: '',
  auth0ClientId: '',
  auth0ClientSecret: '',
  authorizationHost: '',
  smartClientId: '',
  smartClientSecret: '',
  smartRedirectUri: '',
  smartScopes: '',
};

function draftFrom(row: FhirIntegrationViewModel): Draft {
  return {
    name: row.name,
    isDefault: row.isDefault,
    active: row.active,
    skipAuthentication: row.skipAuthentication,
    authorizationCriteria: row.authorizationCriteria,
    patientIdHeaderName: row.patientIdHeaderName,
    accessTokenHeaderName: row.accessTokenHeaderName,
    frontendBaseUrl: row.frontendBaseUrl,
    patientIdSearchCriteria: row.patientIdSearchCriteria,
    patientIdExtensionUrl: row.patientIdExtensionUrl,
    fhirServiceRootUrl: row.fhirServiceRootUrl,
    // Secrets are never returned; always start blank ("keep stored" on save).
    systemAccountUser: '',
    systemAccountPass: '',
    auth0TokenUrl: row.auth0TokenUrl,
    auth0ApiAudience: row.auth0ApiAudience,
    auth0Connection: row.auth0Connection,
    auth0ClientId: '',
    auth0ClientSecret: '',
    authorizationHost: row.authorizationHost,
    // Secrets are never returned; always start blank ("keep stored" on save).
    smartClientId: '',
    smartClientSecret: '',
    smartRedirectUri: row.smartRedirectUri,
    smartScopes: row.smartScopes,
  };
}

/**
 * Maps each editable draft field to its key in the clipboard JSON (snake_case — the same shape the
 * API uses, so an exported config reads like the wire DTO). Read-only fields (id, has_*) are
 * intentionally excluded. The `criteria` kind constrains the value to the two valid strategies.
 */
type ClipboardKind = 'string' | 'boolean' | 'criteria';
interface ClipboardField {
  readonly json: string;
  readonly draft: keyof Draft;
  readonly kind: ClipboardKind;
  /**
   * Write-only credential. Accepted on *import* (pasting a prepared config is the real workflow)
   * but never included in an *export* — "Copy config" must not manufacture a credentials file on
   * the system clipboard, which has cross-process history and cloud sync (PTV2-1495).
   */
  readonly secret?: true;
}

const CLIPBOARD_FIELDS: readonly ClipboardField[] = [
  { json: 'name', draft: 'name', kind: 'string' },
  { json: 'is_default', draft: 'isDefault', kind: 'boolean' },
  { json: 'active', draft: 'active', kind: 'boolean' },
  { json: 'skip_authentication', draft: 'skipAuthentication', kind: 'boolean' },
  { json: 'authorization_criteria', draft: 'authorizationCriteria', kind: 'criteria' },
  { json: 'patient_id_header_name', draft: 'patientIdHeaderName', kind: 'string' },
  { json: 'access_token_header_name', draft: 'accessTokenHeaderName', kind: 'string' },
  { json: 'frontend_base_url', draft: 'frontendBaseUrl', kind: 'string' },
  { json: 'patient_id_search_criteria', draft: 'patientIdSearchCriteria', kind: 'string' },
  { json: 'patient_id_extension_url', draft: 'patientIdExtensionUrl', kind: 'string' },
  { json: 'fhir_service_root_url', draft: 'fhirServiceRootUrl', kind: 'string' },
  { json: 'system_account_user', draft: 'systemAccountUser', kind: 'string' , secret: true },
  { json: 'system_account_pass', draft: 'systemAccountPass', kind: 'string' , secret: true },
  { json: 'auth0_token_url', draft: 'auth0TokenUrl', kind: 'string' },
  { json: 'auth0_api_audience', draft: 'auth0ApiAudience', kind: 'string' },
  { json: 'auth0_connection', draft: 'auth0Connection', kind: 'string' },
  { json: 'auth0_client_id', draft: 'auth0ClientId', kind: 'string' , secret: true },
  { json: 'auth0_client_secret', draft: 'auth0ClientSecret', kind: 'string' , secret: true },
  { json: 'authorization_host', draft: 'authorizationHost', kind: 'string' },
  { json: 'smart_client_id', draft: 'smartClientId', kind: 'string' , secret: true },
  { json: 'smart_client_secret', draft: 'smartClientSecret', kind: 'string' , secret: true },
  { json: 'smart_redirect_uri', draft: 'smartRedirectUri', kind: 'string' },
  { json: 'smart_scopes', draft: 'smartScopes', kind: 'string' },
];

/** Blank, or a parseable absolute URL. Deliberately not stricter — the EHR registration is authority. */
function isBlankOrAbsoluteUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return URL.canParse(trimmed);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCriteria(value: unknown): value is AuthorizationCriteria {
  return value === 'REFRESH_TOKEN' || value === 'SYSTEM_ACCOUNT';
}

/** Serialise the editable, non-secret draft fields to a plain JSON object (snake_case). */
function draftToJson(draft: Draft): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const field of CLIPBOARD_FIELDS) {
    if (field.secret === true) continue;
    out[field.json] = draft[field.draft];
  }
  return out;
}

/** Apply a parsed clipboard object onto the draft. Accepts both snake_case and the draft key. */
function applyJsonToDraft(prev: Draft, parsed: Record<string, unknown>): { draft: Draft; applied: number } {
  const next = { ...prev } as Record<keyof Draft, string | boolean>;
  let applied = 0;
  for (const field of CLIPBOARD_FIELDS) {
    const raw = field.json in parsed ? parsed[field.json] : parsed[field.draft];
    if (raw === undefined) continue;
    if (field.kind === 'boolean') {
      if (typeof raw === 'boolean') {
        next[field.draft] = raw;
        applied += 1;
      }
    } else if (field.kind === 'criteria') {
      if (isCriteria(raw)) {
        next[field.draft] = raw;
        applied += 1;
      }
    } else if (typeof raw === 'string') {
      next[field.draft] = raw;
      applied += 1;
    }
  }
  return { draft: next as unknown as Draft, applied };
}

interface FieldErrors {
  name?: string;
  smartRedirectUri?: string;
}

type ClipboardNote = { readonly kind: 'ok' | 'error'; readonly text: string } | null;

export function FhirIntegrationFormDialog({
  open,
  initial,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  /** Non-null = edit; null = create. */
  readonly initial: FhirIntegrationViewModel | null;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: FhirIntegrationInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [copied, setCopied] = useState(false);
  const [clipboardNote, setClipboardNote] = useState<ClipboardNote>(null);
  const isEdit = initial !== null;

  useEffect(() => {
    if (open) {
      setDraft(initial !== null ? draftFrom(initial) : EMPTY_DRAFT);
      setErrors({});
      setCopied(false);
      setClipboardNote(null);
    }
  }, [open, initial]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((p) => ({ ...p, [key]: value }));
  }

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(draftToJson(draft), null, 2));
      setCopied(true);
      setClipboardNote(null);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      setClipboardNote({ kind: 'error', text: t('routes.admin.fhirIntegrations.entries.form.clipboard.copyError') });
    }
  }

  /**
   * Import on a real paste gesture (Ctrl/Cmd+V) — the paste event carries the clipboard text
   * directly, so there's no browser permission prompt. We only hijack the paste when it parses to
   * one of our exported configs; otherwise it falls through as a normal paste into the focused field.
   */
  function handlePaste(event: ClipboardEvent<HTMLFormElement>): void {
    const text = event.clipboardData.getData('text');
    if (text.trim().length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }
    if (!isRecord(parsed)) return;
    const { draft: nextDraft, applied } = applyJsonToDraft(draft, parsed);
    if (applied === 0) return;
    event.preventDefault();
    setDraft(nextDraft);
    setErrors({});
    setClipboardNote({
      kind: 'ok',
      text: t('routes.admin.fhirIntegrations.entries.form.clipboard.imported', { count: applied }),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    if (draft.name.trim().length === 0)
      next.name = t('routes.admin.fhirIntegrations.entries.form.errors.nameRequired');
    // Blank is valid (the gateway derives it); a typo is not — a URI that fails to parse can never
    // byte-match the EHR registration, and the launch failure it causes is silent.
    if (!isBlankOrAbsoluteUrl(draft.smartRedirectUri))
      next.smartRedirectUri = t(
        'routes.admin.fhirIntegrations.entries.form.errors.smartRedirectUriInvalid',
      );
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      name: draft.name,
      isDefault: draft.isDefault,
      active: draft.active,
      skipAuthentication: draft.skipAuthentication,
      authorizationCriteria: draft.authorizationCriteria,
      patientIdHeaderName: draft.patientIdHeaderName,
      accessTokenHeaderName: draft.accessTokenHeaderName,
      frontendBaseUrl: draft.frontendBaseUrl,
      patientIdSearchCriteria: draft.patientIdSearchCriteria,
      patientIdExtensionUrl: draft.patientIdExtensionUrl,
      fhirServiceRootUrl: draft.fhirServiceRootUrl,
      systemAccountUser: draft.systemAccountUser,
      systemAccountPass: draft.systemAccountPass,
      auth0TokenUrl: draft.auth0TokenUrl,
      auth0ApiAudience: draft.auth0ApiAudience,
      auth0Connection: draft.auth0Connection,
      auth0ClientId: draft.auth0ClientId,
      auth0ClientSecret: draft.auth0ClientSecret,
      authorizationHost: draft.authorizationHost,
      smartClientId: draft.smartClientId,
      smartClientSecret: draft.smartClientSecret,
      smartRedirectUri: draft.smartRedirectUri,
      smartScopes: draft.smartScopes,
    });
  }

  const fieldPrefix = 'routes.admin.fhirIntegrations.entries.form.fields';
  const hintPrefix = 'routes.admin.fhirIntegrations.entries.form.hints';
  const sectionPrefix = 'routes.admin.fhirIntegrations.entries.form.sections';

  const secretPlaceholder = (hasStored: boolean): string | undefined =>
    hasStored ? t('routes.admin.fhirIntegrations.entries.form.secretKeepPlaceholder') : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('routes.admin.fhirIntegrations.entries.edit.title')
              : t('routes.admin.fhirIntegrations.entries.create.title')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          onPaste={handlePaste}
          noValidate
          className="space-y-6 max-h-[70vh] overflow-y-auto pr-1"
        >
          {/* General */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground">{t(`${sectionPrefix}.general`)}</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField
                id="fhir-integration-name"
                label={t(`${fieldPrefix}.name`)}
                hint={t(`${hintPrefix}.name`)}
                required
                error={errors.name}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.name}
                    onChange={(e) => {
                      set('name', e.target.value);
                    }}
                    autoComplete="off"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-service-root-url"
                label={t(`${fieldPrefix}.fhirServiceRootUrl`)}
                hint={t(`${hintPrefix}.fhirServiceRootUrl`)}
                className="sm:col-span-2"
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.fhirServiceRootUrl}
                    onChange={(e) => {
                      set('fhirServiceRootUrl', e.target.value);
                    }}
                    placeholder="https://ehr.example/fhir/r4"
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
            </div>
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(e) => {
                    set('isDefault', e.target.checked);
                  }}
                  className="h-3.5 w-3.5"
                />
                {t(`${fieldPrefix}.isDefault`)}
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(e) => {
                    set('active', e.target.checked);
                  }}
                  className="h-3.5 w-3.5"
                />
                {t(`${fieldPrefix}.active`)}
              </label>
            </div>
          </fieldset>

          {/* SMART EHR launch (PTV2-1495) */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground">{t(`${sectionPrefix}.smart`)}</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                id="fhir-integration-smart-client-id"
                label={t(`${fieldPrefix}.smartClientId`)}
                hint={t(`${hintPrefix}.smartClientId`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    type="password"
                    value={draft.smartClientId}
                    onChange={(e) => {
                      set('smartClientId', e.target.value);
                    }}
                    autoComplete="new-password"
                    placeholder={secretPlaceholder(initial?.hasSmartClientId ?? false)}
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-smart-client-secret"
                label={t(`${fieldPrefix}.smartClientSecret`)}
                hint={t(`${hintPrefix}.smartClientSecret`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    type="password"
                    value={draft.smartClientSecret}
                    onChange={(e) => {
                      set('smartClientSecret', e.target.value);
                    }}
                    autoComplete="new-password"
                    placeholder={secretPlaceholder(initial?.hasSmartClientSecret ?? false)}
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-smart-redirect-uri"
                label={t(`${fieldPrefix}.smartRedirectUri`)}
                hint={t(`${hintPrefix}.smartRedirectUri`)}
                error={errors.smartRedirectUri}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.smartRedirectUri}
                    onChange={(e) => {
                      set('smartRedirectUri', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-smart-scopes"
                label={t(`${fieldPrefix}.smartScopes`)}
                hint={t(`${hintPrefix}.smartScopes`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.smartScopes}
                    onChange={(e) => {
                      set('smartScopes', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-patient-id-header"
                label={t(`${fieldPrefix}.patientIdHeaderName`)}
                hint={t(`${hintPrefix}.patientIdHeaderName`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.patientIdHeaderName}
                    onChange={(e) => {
                      set('patientIdHeaderName', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-access-token-header"
                label={t(`${fieldPrefix}.accessTokenHeaderName`)}
                hint={t(`${hintPrefix}.accessTokenHeaderName`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.accessTokenHeaderName}
                    onChange={(e) => {
                      set('accessTokenHeaderName', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
            </div>
          </fieldset>

          {/* Launch behaviour */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground">{t(`${sectionPrefix}.launch`)}</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField
                id="fhir-integration-criteria"
                label={t(`${fieldPrefix}.authorizationCriteria`)}
                hint={t(`${hintPrefix}.authorizationCriteria`)}
              >
                {(fp) => (
                  <Select
                    {...fp}
                    value={draft.authorizationCriteria}
                    onChange={(e) => {
                      if (isCriteria(e.target.value)) set('authorizationCriteria', e.target.value);
                    }}
                  >
                    {AUTHORIZATION_CRITERIA_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {t(`routes.admin.fhirIntegrations.entries.criteria.${opt}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField
                id="fhir-integration-frontend-base-url"
                label={t(`${fieldPrefix}.frontendBaseUrl`)}
                hint={t(`${hintPrefix}.frontendBaseUrl`)}
                className="sm:col-span-3"
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.frontendBaseUrl}
                    onChange={(e) => {
                      set('frontendBaseUrl', e.target.value);
                    }}
                    placeholder="https://app.example"
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-patient-id-search-criteria"
                label={t(`${fieldPrefix}.patientIdSearchCriteria`)}
                hint={
                  draft.patientIdSearchCriteria === 'systemValue'
                    ? t(`${hintPrefix}.patientIdSearchCriteriaSystemValue`)
                    : t(`${hintPrefix}.patientIdSearchCriteria`)
                }
              >
                {(fp) => (
                  <Select
                    {...fp}
                    value={draft.patientIdSearchCriteria}
                    onChange={(e) => {
                      set('patientIdSearchCriteria', e.target.value);
                    }}
                  >
                    <option value="">{t(`${fieldPrefix}.patientIdSearchCriteriaNone`)}</option>
                    <option value="extensionURL">
                      {t(`${fieldPrefix}.patientIdSearchCriteriaExtensionUrl`)}
                    </option>
                    <option value="systemValue">
                      {t(`${fieldPrefix}.patientIdSearchCriteriaSystemValue`)}
                    </option>
                  </Select>
                )}
              </FormField>
              <FormField
                id="fhir-integration-patient-id-extension-url"
                label={t(`${fieldPrefix}.patientIdExtensionUrl`)}
                hint={t(`${hintPrefix}.patientIdExtensionUrl`)}
                className="sm:col-span-2"
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.patientIdExtensionUrl}
                    onChange={(e) => {
                      set('patientIdExtensionUrl', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.skipAuthentication}
                onChange={(e) => {
                  set('skipAuthentication', e.target.checked);
                }}
                className="h-3.5 w-3.5"
              />
              {t(`${fieldPrefix}.skipAuthentication`)}
            </label>
          </fieldset>

          {/* System account (secrets) */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground">
              {t(`${sectionPrefix}.systemAccount`)}
            </legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                id="fhir-integration-system-account-user"
                label={t(`${fieldPrefix}.systemAccountUser`)}
                hint={t(`${hintPrefix}.systemAccountUser`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    type="password"
                    value={draft.systemAccountUser}
                    onChange={(e) => {
                      set('systemAccountUser', e.target.value);
                    }}
                    autoComplete="new-password"
                    placeholder={secretPlaceholder(initial?.hasSystemAccountUser ?? false)}
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-system-account-pass"
                label={t(`${fieldPrefix}.systemAccountPass`)}
                hint={t(`${hintPrefix}.systemAccountPass`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    type="password"
                    value={draft.systemAccountPass}
                    onChange={(e) => {
                      set('systemAccountPass', e.target.value);
                    }}
                    autoComplete="new-password"
                    placeholder={secretPlaceholder(initial?.hasSystemAccountPass ?? false)}
                  />
                )}
              </FormField>
            </div>
          </fieldset>

          {/* Auth0 M2M */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground">{t(`${sectionPrefix}.auth0`)}</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField
                id="fhir-integration-auth0-token-url"
                label={t(`${fieldPrefix}.auth0TokenUrl`)}
                hint={t(`${hintPrefix}.auth0TokenUrl`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.auth0TokenUrl}
                    onChange={(e) => {
                      set('auth0TokenUrl', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-auth0-api-audience"
                label={t(`${fieldPrefix}.auth0ApiAudience`)}
                hint={t(`${hintPrefix}.auth0ApiAudience`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.auth0ApiAudience}
                    onChange={(e) => {
                      set('auth0ApiAudience', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-auth0-connection"
                label={t(`${fieldPrefix}.auth0Connection`)}
                hint={t(`${hintPrefix}.auth0Connection`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.auth0Connection}
                    onChange={(e) => {
                      set('auth0Connection', e.target.value);
                    }}
                    autoComplete="off"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-authorization-host"
                label={t(`${fieldPrefix}.authorizationHost`)}
                hint={t(`${hintPrefix}.authorizationHost`)}
                className="sm:col-span-3"
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={draft.authorizationHost}
                    onChange={(e) => {
                      set('authorizationHost', e.target.value);
                    }}
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-auth0-client-id"
                label={t(`${fieldPrefix}.auth0ClientId`)}
                hint={t(`${hintPrefix}.auth0ClientId`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    type="password"
                    value={draft.auth0ClientId}
                    onChange={(e) => {
                      set('auth0ClientId', e.target.value);
                    }}
                    autoComplete="new-password"
                    placeholder={secretPlaceholder(initial?.hasAuth0ClientId ?? false)}
                  />
                )}
              </FormField>
              <FormField
                id="fhir-integration-auth0-client-secret"
                label={t(`${fieldPrefix}.auth0ClientSecret`)}
                hint={t(`${hintPrefix}.auth0ClientSecret`)}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    type="password"
                    value={draft.auth0ClientSecret}
                    onChange={(e) => {
                      set('auth0ClientSecret', e.target.value);
                    }}
                    autoComplete="new-password"
                    placeholder={secretPlaceholder(initial?.hasAuth0ClientSecret ?? false)}
                  />
                )}
              </FormField>
            </div>
          </fieldset>


          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.fhirIntegrations.entries.form.errors.submit', {
                code: getErrorCodeForUi(submitError),
              })}
            </p>
          )}

          <DialogFooter className="sm:items-center sm:justify-between sm:space-x-0">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied
                  ? t('routes.admin.fhirIntegrations.entries.form.clipboard.copied')
                  : t('routes.admin.fhirIntegrations.entries.form.clipboard.copy')}
              </Button>
              {clipboardNote !== null ? (
                <span
                  role={clipboardNote.kind === 'error' ? 'alert' : undefined}
                  className={
                    clipboardNote.kind === 'error' ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'
                  }
                >
                  {clipboardNote.text}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t('routes.admin.fhirIntegrations.entries.form.clipboard.pasteHint')}
                </span>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={pending}
              >
                {t('routes.admin.fhirIntegrations.entries.form.cancel')}
              </Button>
              <Button type="submit" loading={pending}>
                {isEdit
                  ? t('routes.admin.fhirIntegrations.entries.edit.submit')
                  : t('routes.admin.fhirIntegrations.entries.create.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
